/* ============================================================
 * <lion-video> — lightweight, privacy-friendly video embed.
 * - Native Shopify video: handles autoplay with IntersectionObserver
 *   (pauses off-screen to save CPU and data).
 * - External URL (YouTube/Vimeo): click-to-play poster, iframe
 *   only loaded on demand so it doesn't ship unused kB.
 * ============================================================ */

class LionVideo extends HTMLElement {
  constructor() {
    super();
    this.autoplay = this.dataset.autoplay === 'true';
    this.src = this.dataset.src;
    this.provider = this.dataset.provider;
  }

  connectedCallback() {
    const videoEl = this.querySelector('video');
    if (videoEl) this.initNativeVideo(videoEl);

    const playBtn = this.querySelector('[data-video-play]');
    if (playBtn && this.src) playBtn.addEventListener('click', () => this.loadEmbed());
  }

  initNativeVideo(videoEl) {
    if (!this.autoplay) return;
    if (!('IntersectionObserver' in window)) { videoEl.play().catch(() => {}); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) videoEl.play().catch(() => {});
        else videoEl.pause();
      });
    }, { threshold: 0.25 });
    io.observe(videoEl);
  }

  loadEmbed() {
    if (!this.src) return;
    const iframe = document.createElement('iframe');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    iframe.setAttribute('title', this.getAttribute('aria-label') || 'Embedded video');
    iframe.className = 'video-section__embed';

    let url = this.src;
    if (this.provider === 'youtube') {
      url = url.replace('watch?v=', 'embed/') + (url.includes('?') ? '&' : '?') + 'autoplay=1&rel=0';
    } else if (this.provider === 'vimeo') {
      url = url.replace('vimeo.com/', 'player.vimeo.com/video/') + '?autoplay=1';
    }
    iframe.src = url;
    this.querySelector('[data-video-play]')?.remove();
    this.appendChild(iframe);
  }
}
if (!customElements.get('lion-video')) customElements.define('lion-video', LionVideo);
